import * as React from "react";

import { AsyncComponent } from "@impero-com/common/components/asyncComponent";
import {
    ColumnBuilder,
    ColumnOptions,
    DATA_TABLE_MODE_VIEWPORT,
    DEFAULT_COLUMN_BUILDER_ID,
    DataTable,
} from "@impero-com/common/components/dataTable";
import { TableFilter } from "@impero-com/common/components/filter";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import createIcon from "@impero-com/common/img/create-icon.svg";
import exportIcon from "@impero-com/common/img/export.svg";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import * as Cmp from "@impero-com/common/utils/comparison";
import {
    DataView,
    DataExtractor,
    getAllExtractors,
    ArrayDataSource,
    ExtractorExpander,
} from "@impero-com/common/utils/dataView";
import * as DV from "@impero-com/common/utils/dataView";
import { t } from "@impero-com/common/utils/i18n";
import {
    DataViewIndex,
    SimpleAndFilter,
    QueryResult,
    addFieldFilter,
    removeFieldFilter,
    SimpleFieldFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";
import {
    actionMenuStatus,
    ActionSpec,
    ContentWithoutBorder,
    PageSpinner,
} from "@impero-com/ui";
import { Typography } from "antd";
import memoizeOne from "memoize-one";

import type { UserProfile } from "api/generated/model";
import {
    AvailableActions,
    UserTagOverviewListDto as Tags,
    UserTagCategoryWithTagsView,
    UserTagOverview,
} from "api/generated/model";
import { getTags } from "api/generated/tags/tags";
import shareIcon from "img/share-icon.svg";
import { PageStateManager } from "org/pageState/components/pageStateManager";
import { pageStateTagsCodecs } from "org/pageState/convert/tags";
import { IdAndName } from "org/pageState/models";
import { PAGE_KEY } from "org/pageState/pageKey";
import { PageFilterAndColumns } from "org/pageState/types";
import { splitPageStateWithCallback } from "org/pageState/utils";
import { downgradeExtractorType } from "utils/dataView";
import { Guid } from "utils/guid";
import { createCategoryNameLookupFn, createTagNameLookupFn } from "utils/tag";

import { TagsListApplicationBar } from "./applicationBar";
import { CreateTagCategoryDrawer } from "./createDrawer";
import { EditTagCategoryDrawer } from "./editDrawer";
import * as theme from "./theme.css";

interface TagsListProps {
    ajaxManager: AjaxManager;
    orgCookie: OrgCookie;
    orgProfileCookie: UserProfile | null;
    availableActions: AvailableActions;
}

interface TagsListState {
    status: Status;
}

type Status =
    | StatusDefault
    | StatusEditCategoryDrawer
    | StatusCreateCategoryDrawer;

interface StatusDefault {
    type: "DEFAULT";
}

interface StatusEditCategoryDrawer {
    type: "EDIT_CATEGORY_DRAWER";
    category: UserTagCategoryWithTagsView;
}

interface StatusCreateCategoryDrawer {
    type: "CREATE_CATEGORY_DRAWER";
}

class TagsList extends React.Component<TagsListProps, TagsListState> {
    private asyncComponent = React.createRef<AsyncComponent<Tags>>();

    constructor(props: TagsListProps) {
        super(props);
        this.state = {
            status: { type: "DEFAULT" },
        };
    }

    private memoizeCategoryNames: (allTags: Tags) => Array<string> = memoizeOne(
        (allTags: Tags) => {
            const categories = allTags.linkedResources;
            const activeCategories = categories.filter((cat) => cat.active);
            return activeCategories.map((cat) => cat.name);
        }
    );

    private memoizeDataView = memoizeOne(
        (
            allTags: Tags
        ): [
            DataView<UserTagCategoryWithTagsView>,
            DataViewIndex<UserTagCategoryWithTagsView>,
        ] => {
            const tagCategoriesByIds: Record<
                Guid,
                UserTagCategoryWithTagsView
            > = {};

            allTags.linkedResources.forEach((category) => {
                if (category.active) {
                    tagCategoriesByIds[category.id] = {
                        category,
                        tags: [],
                    };
                }
            });

            allTags.content.forEach((tag) => {
                if (
                    tagCategoriesByIds[tag.resource.tagCategoryId] &&
                    tag.resource.active
                ) {
                    tagCategoriesByIds[tag.resource.tagCategoryId].tags.push(
                        tag.resource
                    );
                }
            });

            const data: Array<UserTagCategoryWithTagsView> =
                Object.values(tagCategoriesByIds);

            data.sort(Cmp.map((row) => row.category.name, Cmp.string()));
            data.forEach((category) => {
                category.tags.sort(Cmp.map((row) => row.name, Cmp.string()));
            });

            const standardExtractors: Array<
                DataExtractor<UserTagCategoryWithTagsView, unknown>
            > = [
                downgradeExtractorType(
                    this.buildCategoryNameExtractor(allTags)
                ),
                downgradeExtractorType(this.buildCountExtractor()),
                downgradeExtractorType(
                    this.buildOuterTagNameExtractor(allTags)
                ),
            ];

            const dataView: DataView<UserTagCategoryWithTagsView> = {
                dataSource: new ArrayDataSource(data, (row) => row.category.id),
                extractorGroups: [
                    {
                        id: "standard",
                        label: "Standard",
                        extractors: standardExtractors,
                    },
                ],
                sorters: [{ extractorId: "categoryName", direction: "ASC" }],
                defaultCompareItems: Cmp.map(
                    (row) => row.category.id,
                    Cmp.primitive
                ),
            };

            const expanders: Array<
                ExtractorExpander<UserTagOverview[], UserTagOverview>
            > = [
                {
                    extractorId: "tagName",
                    expand: (tags: Array<UserTagOverview>) =>
                        tags.map((tag) => {
                            return {
                                partialId: `tag-${tag.id}`,
                                expandedValue: tag,
                            };
                        }),
                    asString: (tag) => tag.name,
                    compare: Cmp.map((tag) => tag.id, Cmp.primitive),
                    customFilterTransformer: {
                        getValue: (tag: UserTagOverview) => tag,
                        asString: (tag) => tag.name || "",
                    },
                },
            ];

            return [
                dataView,
                new DataViewIndex(dataView, undefined, expanders),
            ];
        }
    );

    render = () => {
        const { ajaxManager, availableActions } = this.props;

        const renderError = (code: number | undefined) => {
            return (
                <TagsListApplicationBar currentTabId="tab-tags">
                    {code === 403 ? (
                        <NoAccessCard />
                    ) : (
                        <LoggedInHTTPError code={code} />
                    )}
                </TagsListApplicationBar>
            );
        };
        const renderLoading = () => <PageSpinner />;

        return (
            <AsyncComponent<Tags>
                ref={this.asyncComponent}
                ajaxManager={ajaxManager}
                promise={getTags}
                key={"tags"}
                renderLoading={renderLoading}
                renderError={renderError}
                renderContent={(allTags: Tags) => {
                    return (
                        <PageStateManager<"Tags", PageFilterAndColumns>
                            pageKey={PAGE_KEY.tags()}
                            renderLoading={renderLoading}
                            renderError={renderError}
                            codecs={pageStateTagsCodecs}
                            renderContent={({
                                pageState,
                                onPageStateChange,
                                onCreateShareableLink,
                            }) => {
                                const {
                                    filter,
                                    onFilterChange,
                                    visibleColumnIds,
                                    onVisibleColumnsChange,
                                } = splitPageStateWithCallback(
                                    pageState,
                                    onPageStateChange
                                );
                                const [dataView, dataViewIndex] =
                                    this.memoizeDataView(allTags);
                                const queryResult = this.buildQueryResult(
                                    dataViewIndex,
                                    filter
                                );

                                // Filter out tags if tag filters are found
                                const expandedRowFilter =
                                    this.expandedRowFilter(filter);
                                if (expandedRowFilter !== null) {
                                    const expandedRowDataViewBuilder =
                                        this.expandedRowDataViewBuilder(
                                            allTags
                                        );

                                    const items = DV.toArray(
                                        queryResult.dataView.dataSource.iter()
                                    );
                                    items.forEach((item, i) => {
                                        let rowDataView =
                                            expandedRowDataViewBuilder(item);
                                        if (rowDataView) {
                                            const index = new DataViewIndex(
                                                rowDataView,
                                                DV.getAllExtractors(
                                                    rowDataView
                                                ).map(
                                                    (extractor) => extractor.id
                                                )
                                            );
                                            rowDataView =
                                                index.find(
                                                    expandedRowFilter
                                                ).dataView;
                                        }
                                        items[i] = {
                                            ...item,
                                            tags: rowDataView
                                                ? DV.toArray(
                                                      rowDataView.dataSource.iter()
                                                  )
                                                : [],
                                        };
                                    });

                                    // Overwrite the dataview but keep the main index to allow ORs
                                    queryResult.dataView = {
                                        ...dataView,
                                        dataSource: new DV.ArrayDataSource(
                                            items,
                                            dataView.dataSource.getId
                                        ),
                                    };
                                }

                                const onAddFilter = (
                                    extractorId: string,
                                    fieldFilter: SimpleFieldFilter,
                                    excluded: boolean
                                ) => {
                                    if (onFilterChange) {
                                        onFilterChange(
                                            addFieldFilter(
                                                extractorId,
                                                fieldFilter,
                                                excluded,
                                                filter
                                            )
                                        );
                                    }
                                };

                                const onRemoveFilter = (
                                    extractorId: string,
                                    fieldFilter: SimpleFieldFilter,
                                    excluded: boolean
                                ) => {
                                    if (onFilterChange && filter) {
                                        onFilterChange(
                                            removeFieldFilter(
                                                extractorId,
                                                fieldFilter,
                                                excluded,
                                                filter
                                            )
                                        );
                                    }
                                };

                                const onResetFilter = () => {
                                    if (onFilterChange) {
                                        onFilterChange(null);
                                    }
                                };

                                const actions: ActionSpec[] = [];
                                if (availableActions.userTag.create) {
                                    actions.push({
                                        id: "tags_export_action",
                                        label: t("TagsList_Action_Export"),
                                        icon: { svg: exportIcon },
                                        onClick: () =>
                                            this.onExport(queryResult),
                                    });
                                }
                                if (
                                    availableActions.userTag.create &&
                                    onCreateShareableLink
                                ) {
                                    actions.push({
                                        id: "shareable-link-create-new",
                                        label: t("ShareableLink_CreateNew"),
                                        icon: { svg: shareIcon },
                                        onClick: onCreateShareableLink,
                                    });
                                }
                                actions.push({
                                    id: "create-new-tag-category",
                                    label: t(
                                        "TagsList_Action_CreateNewTagCategory"
                                    ),
                                    icon: { svg: createIcon },
                                    onClick: () => this.setStatusCreateDrawer(),
                                    status: actionMenuStatus(
                                        availableActions.userTag.create,
                                        t("TagsList_Edit_Forbidden")
                                    ),
                                });

                                return (
                                    <TagsListApplicationBar
                                        currentTabId="tab-tags"
                                        actions={actions}
                                    >
                                        {availableActions.userTag.create ? (
                                            <ContentWithoutBorder
                                                className={theme.whiteBg}
                                            >
                                                <TableFilter<UserTagCategoryWithTagsView>
                                                    id="filter"
                                                    queryResult={queryResult}
                                                    dataViewIndex={
                                                        dataViewIndex
                                                    }
                                                    filter={filter}
                                                    extractorRefs={getAllExtractors(
                                                        dataView
                                                    ).map((ext) => ({
                                                        extractorId: ext.id,
                                                    }))}
                                                    onAddFilter={onAddFilter}
                                                    onRemoveFilter={
                                                        onRemoveFilter
                                                    }
                                                    onResetFilter={
                                                        onResetFilter
                                                    }
                                                />
                                                <DataTable<UserTagCategoryWithTagsView>
                                                    id="tags"
                                                    mode={
                                                        DATA_TABLE_MODE_VIEWPORT
                                                    }
                                                    expandedRowRender={this.expandedRowWithFilterRenderer(
                                                        allTags
                                                    )}
                                                    dataView={
                                                        queryResult.dataView
                                                    }
                                                    columnBuilders={this.buildColumns()}
                                                    visibleColumnIds={
                                                        visibleColumnIds
                                                    }
                                                    onVisibleColumnsChanged={
                                                        onVisibleColumnsChange
                                                    }
                                                    getRowClassName={
                                                        this.getRowClassName
                                                    }
                                                    expandedRowClassName={
                                                        this
                                                            .getExpandedRowClassName
                                                    }
                                                />
                                                {this.renderDrawerIfNecessary()}
                                            </ContentWithoutBorder>
                                        ) : (
                                            <NoAccessCard />
                                        )}
                                    </TagsListApplicationBar>
                                );
                            }}
                        />
                    );
                }}
            />
        );
    };

    getExpandedRowClassName = () => {
        return theme.expandedRow;
    };

    private renderDrawerIfNecessary = (): React.ReactNode => {
        const { status } = this.state;

        switch (status.type) {
            case "DEFAULT":
                return null;
            case "EDIT_CATEGORY_DRAWER": {
                const onClose = (isChanged: boolean) => {
                    this.setStatusDefault();
                    if (isChanged) {
                        this.asyncComponent.current?.reload();
                    }
                };
                const onTagCategoryDeleted = (_deletedCategoryId: string) => {
                    this.setStatusDefault();
                    this.asyncComponent.current?.reload();
                };
                return (
                    <EditTagCategoryDrawer
                        category={status.category.category}
                        tags={status.category.tags}
                        onClose={onClose}
                        onTagCategoryDeleted={onTagCategoryDeleted}
                    />
                );
            }
            case "CREATE_CATEGORY_DRAWER": {
                const allTags = this.asyncComponent.current?.getData();
                const categoryNames = allTags
                    ? this.memoizeCategoryNames(allTags)
                    : [];

                const onCategoryCreated = (
                    _createdCategory: UserTagCategoryWithTagsView
                ) => {
                    this.setStatusDefault();
                    this.asyncComponent.current?.reload();
                };

                return (
                    <CreateTagCategoryDrawer
                        existingTagCategoryNames={categoryNames}
                        onCancel={() => this.setStatusDefault()}
                        onCreated={onCategoryCreated}
                    />
                );
            }
            default:
                return assertNever(status);
        }
    };

    private setStatusDefault = (): void => {
        this.setState({
            status: { type: "DEFAULT" },
        });
    };

    private setStatusEditDrawer = (
        category: UserTagCategoryWithTagsView
    ): void => {
        this.setState({
            status: {
                type: "EDIT_CATEGORY_DRAWER",
                category,
            },
        });
    };

    private setStatusCreateDrawer = (): void => {
        this.setState({
            status: { type: "CREATE_CATEGORY_DRAWER" },
        });
    };

    private buildQueryResult = memoizeOne(
        (
            dataViewIndex: DataViewIndex<UserTagCategoryWithTagsView>,
            filter: SimpleAndFilter | null
        ): QueryResult<UserTagCategoryWithTagsView> => {
            return dataViewIndex.find(filter);
        }
    );

    private getRowClassName = (item: UserTagCategoryWithTagsView): string => {
        if (item.tags.length === 0) {
            return theme.disableExpandButton;
        }
        return "";
    };

    private buildColumns = (): {
        [extractorId: string]: ColumnBuilder<UserTagCategoryWithTagsView, any>;
    } => {
        return {
            categoryName: (
                _extractor
            ): ColumnOptions<UserTagCategoryWithTagsView> => ({
                getFormattedValue: (tagCategoryRow) => (
                    <Typography.Link
                        onClick={() => this.setStatusEditDrawer(tagCategoryRow)}
                    >
                        <span>{tagCategoryRow.category.name}</span>
                    </Typography.Link>
                ),
            }),
            categoryCount: (
                _extractor
            ): ColumnOptions<UserTagCategoryWithTagsView> => ({
                getFormattedValue: (tagCategoryRow) => (
                    <span>{tagCategoryRow.tags.length}</span>
                ),
            }),
            [DEFAULT_COLUMN_BUILDER_ID]: (extractor) => {
                if (
                    extractor.id.startsWith("onlySearch") ||
                    extractor.id === "tagName"
                ) {
                    return {
                        display: "NEVER",
                    };
                }
                return {
                    display: "DEFAULT_VISIBLE",
                };
            },
        };
    };

    private buildCategoryNameExtractor = (
        allTags: Tags
    ): DataExtractor<
        UserTagCategoryWithTagsView,
        string,
        IdAndName<Guid, string>
    > => {
        const extractValue = (tagCategory: UserTagCategoryWithTagsView) =>
            tagCategory.category.name;
        const categoryNameLookup = createCategoryNameLookupFn(allTags);

        return {
            id: "categoryName",
            label: t("TagsList_Column_Name"),
            getValue: extractValue,
            compare: Cmp.map(extractValue, Cmp.string()),
            asString: extractValue,
            customFilterTransformer: {
                getValue: (tagCategory) => ({
                    id: tagCategory.category.id,
                    name: tagCategory.category.name,
                }),
                asString: categoryNameLookup,
            },
        };
    };

    private buildCountExtractor = (): DataExtractor<
        UserTagCategoryWithTagsView,
        number
    > => {
        const extractValue = (tagCategory: UserTagCategoryWithTagsView) =>
            tagCategory.tags.length;

        return {
            id: "categoryCount",
            label: t("TagsList_Column_Count"),
            getValue: extractValue,
            compare: Cmp.map(extractValue, Cmp.primitive),
            asString: (tagCategory) => extractValue(tagCategory).toString(),
            customFilterTransformer: {
                getValue: extractValue,
                asString: (count) => count.toString(),
            },
        };
    };

    private buildOuterTagNameExtractor = (
        allTags: Tags
    ): DataExtractor<
        UserTagCategoryWithTagsView,
        UserTagOverview[],
        IdAndName<string, string>
    > => {
        const extractTags = (
            category: UserTagCategoryWithTagsView
        ): UserTagOverview[] => category.tags || [];
        const countTags = (category: UserTagCategoryWithTagsView): number =>
            extractTags(category).length;
        const tagNameLookup = createTagNameLookupFn(allTags);

        return {
            id: "tagName",
            label: t("TagsList_Column_Tags"),
            getValue: extractTags,
            compare: Cmp.map(countTags, Cmp.primitive),
            asString: (category) => {
                const tags = extractTags(category);
                return tags.map((tag) => tag.name).join(", ");
            },
            customFilterTransformer: {
                getValue: (_category: UserTagCategoryWithTagsView) => {
                    throw new Error(
                        "Unexpected call of customFilterTransformer.getValue() on tagCategories extractor."
                    );
                },
                asString: tagNameLookup,
            },
        };
    };

    private buildInnerColumns = (): {
        [extractorId: string]: ColumnBuilder<UserTagOverview, any>;
    } => {
        return {
            tagName: (_extractor): ColumnOptions<UserTagOverview> => ({
                getFormattedValue: (tagRow) => <span>{tagRow.name}</span>,
            }),
            [DEFAULT_COLUMN_BUILDER_ID]: (extractor) => {
                if (extractor.id.startsWith("onlySearch")) {
                    return {
                        display: "NEVER",
                    };
                }
                return {
                    display: "DEFAULT_VISIBLE",
                };
            },
        };
    };

    private buildTagNameExtractor = (
        allTags: Tags
    ): DataExtractor<UserTagOverview, string, IdAndName<string, string>> => {
        const tagNameLookup = createTagNameLookupFn(allTags);

        return {
            id: "tagName",
            label: t("TagsList_Column_Tags"),
            getValue: (tag) => tag.name,
            compare: Cmp.map((tag) => tag.name, Cmp.string()),
            asString: (_tag) => {
                throw new Error(
                    "Unexpected call of asString() on tags extractor."
                );
            },
            customFilterTransformer: {
                getValue: (tag) => ({ id: tag.id, name: tag.name }),
                asString: tagNameLookup,
            },
        };
    };

    private expandedRowFilter = (filter: SimpleAndFilter | null) => {
        if (filter) {
            const allowedExtractorIds = ["tagName"];

            const expandedRowFilter: SimpleAndFilter = {
                filters: filter.filters.filter((simpleOrFilter) =>
                    allowedExtractorIds.includes(simpleOrFilter.extractorId)
                ),
            };

            if (expandedRowFilter.filters.length > 0) {
                return expandedRowFilter;
            }
        }

        return null;
    };

    private expandedRowDataViewBuilder = memoizeOne(
        (
            allTags: Tags
        ): ((
            item: UserTagCategoryWithTagsView
        ) => DataView<UserTagOverview> | undefined) => {
            const allExtractors = [this.buildTagNameExtractor(allTags)];

            return (
                item: UserTagCategoryWithTagsView
            ): DataView<UserTagOverview> | undefined => {
                const tags = item.tags;
                if (tags.length === 0) {
                    return undefined;
                }

                const dataView: DataView<UserTagOverview> = {
                    dataSource: new ArrayDataSource(tags, (row) => row.id),
                    extractorGroups: [
                        {
                            id: "standard",
                            label: "Standard",
                            extractors: allExtractors,
                        },
                    ],
                    sorters: [{ extractorId: "tagName", direction: "ASC" }],
                    defaultCompareItems: Cmp.map(
                        (row) => row.id,
                        Cmp.primitive
                    ),
                };

                return dataView;
            };
        }
    );

    private expandedRowWithFilterRenderer = memoizeOne(
        (
            allTags: Tags
        ): ((
            item: UserTagCategoryWithTagsView
        ) => React.ReactNode | undefined) => {
            const expandedRowDataViewBuilder =
                this.expandedRowDataViewBuilder(allTags);

            return (item: UserTagCategoryWithTagsView) => {
                const dataView = expandedRowDataViewBuilder(item);

                if (dataView === undefined) {
                    return undefined;
                }

                return (
                    // <div> is needed because of a class which sets margins on the DataTable
                    <div>
                        <DataTable
                            id="assignments"
                            columnBuilders={this.buildInnerColumns()}
                            dataView={dataView}
                        />
                    </div>
                );
            };
        }
    );

    private onExport = (
        queryResult: QueryResult<UserTagCategoryWithTagsView>
    ) => {
        const { orgCookie, orgProfileCookie } = this.props;

        if (orgProfileCookie === null) {
            throw new Error("orgProfileCookie is null");
        }

        DV.exportToExcel<any>(
            queryResult.dataView,
            () => true,
            getAllExtractors(queryResult.dataView).map((extractor) => ({
                label: extractor.label,
                getValue: extractor.asString,
            })),
            "tags",
            "xlsx",
            "Tags",
            {
                date: new Date(),
                organization: {
                    name: orgCookie.name,
                },
                user: {
                    firstName: orgProfileCookie.firstName,
                    lastName: orgProfileCookie.lastName,
                    email: orgProfileCookie.email,
                },
            }
        );
    };
}

export { TagsList, type TagsListProps };
